// src/ChatPanel.jsx
// Panneau de chat en temps réel avec protections anti-spam

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';

// 🛡️ Liste de mots interdits (à personnaliser)
const BLOCKED_WORDS = [
  'con', 'connard', 'connasse', 'putain', 'merde', 'enculé', 
  'salope', 'pute', 'bite', 'couille', 'chier', 'emmerde'
  // Ajoutez d'autres mots selon vos besoins
];

function ChatPanel({ matchId, isScorer = false, playerName = null, match = null }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // 🆕 Gérer le prénom du spectateur/noteur (sauvegardé en localStorage)
  const [senderName, setSenderName] = useState(() => {
    // Charger depuis localStorage si existe
    const storageKey = isScorer ? 'scorer_name' : 'spectator_name';
    const savedName = localStorage.getItem(storageKey);
    
    if (isScorer) {
      return savedName || '';  // Vide si pas encore renseigné
    } else {
      return savedName || playerName || '';
    }
  });
  
  const [showNamePrompt, setShowNamePrompt] = useState(!senderName);
  
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // 🎨 Palettes d'émojis
  const emojiPalettes = {
    reactions: ['👍', '👎', '👏', '🔥', '💪', '😊', '😂', '😍', '🤔', '😢'],
    tennis: ['🎾', '🏆', '🥇', '🥈', '🥉', '⚡', '💥', '🎯', '🚀', '⭐'],
    general: ['❤️', '✅', '❌', '⚠️', '💡', '🎉', '🙌', '👀', '💬', '📢']
  };
  
  // 🛡️ Protections anti-spam
  const [lastSentTime, setLastSentTime] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const [warningMessage, setWarningMessage] = useState('');
  
  const messagesEndRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  
  const COOLDOWN_MS = 3000; // 3 secondes
  const RATE_LIMIT_WINDOW = 60000; // 1 minute
  const RATE_LIMIT_MAX = 5; // 5 messages max par minute
  
  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Timer cooldown
  useEffect(() => {
    if (cooldownRemaining > 0) {
      cooldownTimerRef.current = setTimeout(() => {
        setCooldownRemaining(prev => Math.max(0, prev - 100));
      }, 100);
    }
    return () => clearTimeout(cooldownTimerRef.current);
  }, [cooldownRemaining]);
  
  // Fonction de chargement des messages (useCallback pour éviter le warning)
  const loadMessages = useCallback(async () => {
    console.log('💬 Chargement des messages du match:', matchId);
    
    const { data, error } = await supabase
      .from('match_messages')
      .select('*')
      .eq('match_id', matchId)
      .order('created_at', { ascending: true })
      .limit(100);
    
    if (error) {
      console.error('❌ Erreur chargement messages:', error);
      return;
    }
    
    console.log('✅ Messages chargés:', data?.length || 0);
    if (data) {
      setMessages(data);
    }
  }, [matchId]);
  
  // Charger les messages existants
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);
  
  // S'abonner aux nouveaux messages en temps réel
  useEffect(() => {
    console.log('💬 Abonnement au chat du match:', matchId);
    
    const channel = supabase
      .channel(`match_chat:${matchId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'match_messages',
        filter: `match_id=eq.${matchId}`
      }, (payload) => {
        console.log('💬 Nouveau message reçu:', payload.new);
        const newMsg = payload.new;
        
        setMessages(prev => {
          // Éviter les doublons
          if (prev.some(m => m.id === newMsg.id)) {
            return prev;
          }
          return [...prev, newMsg];
        });
        
        // Incrémenter compteur non-lus si panneau fermé
        if (!isOpen) {
          setUnreadCount(prev => prev + 1);
        }
      })
      .subscribe();
    
    // 🆕 Détecter quand l'app revient de veille
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 App revenue au premier plan, rechargement messages...');
        loadMessages();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      console.log('💬 Déconnexion du chat');
      channel.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [matchId, isOpen, loadMessages]);
  
  // 🛡️ Vérifier si message contient insultes
  const containsProfanity = (text) => {
    const lowerText = text.toLowerCase();
    return BLOCKED_WORDS.some(word => {
      // Vérifier le mot entier (avec espaces ou ponctuation autour)
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerText);
    });
  };
  
  // 🛡️ Vérifier rate limiting
  const checkRateLimit = () => {
    const now = Date.now();
    
    // Nettoyer les messages anciens (> 1 minute)
    const recentOnes = recentMessages.filter(
      timestamp => now - timestamp < RATE_LIMIT_WINDOW
    );
    setRecentMessages(recentOnes);
    
    // Vérifier si limite atteinte
    if (recentOnes.length >= RATE_LIMIT_MAX) {
      const oldestMessage = Math.min(...recentOnes);
      const waitTime = Math.ceil((RATE_LIMIT_WINDOW - (now - oldestMessage)) / 1000);
      return {
        allowed: false,
        reason: `Trop de messages ! Attendez ${waitTime}s`
      };
    }
    
    return { allowed: true };
  };
  
  // 🛡️ Vérifier cooldown
  const checkCooldown = () => {
    const now = Date.now();
    const timeSinceLastSent = now - lastSentTime;
    
    if (timeSinceLastSent < COOLDOWN_MS) {
      const remaining = Math.ceil((COOLDOWN_MS - timeSinceLastSent) / 1000);
      return {
        allowed: false,
        reason: `Attendez ${remaining}s avant d'envoyer un autre message`
      };
    }
    
    return { allowed: true };
  };
  
  // 🛡️ Vérifier spam (message identique récent)
  const checkSpam = (text) => {
    // Vérifier si même message dans les 5 derniers messages
    const lastFiveMessages = messages.slice(-5);
    const currentDisplayName = getDisplayName();
    const isDuplicate = lastFiveMessages.some(
      m => m.message === text && m.sender_name === currentDisplayName
    );
    
    if (isDuplicate) {
      return {
        allowed: false,
        reason: 'Message déjà envoyé récemment'
      };
    }
    
    return { allowed: true };
  };
  
  const sendMessage = async () => {
    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage) return;
    
    // 🛡️ PROTECTIONS
    
    // 1. Vérifier cooldown
    const cooldownCheck = checkCooldown();
    if (!cooldownCheck.allowed) {
      setWarningMessage(cooldownCheck.reason);
      setTimeout(() => setWarningMessage(''), 3000);
      return;
    }
    
    // 2. Vérifier rate limiting
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      setWarningMessage(rateLimitCheck.reason);
      setTimeout(() => setWarningMessage(''), 5000);
      return;
    }
    
    // 3. Vérifier spam
    const spamCheck = checkSpam(trimmedMessage);
    if (!spamCheck.allowed) {
      setWarningMessage(spamCheck.reason);
      setTimeout(() => setWarningMessage(''), 3000);
      return;
    }
    
    // 4. Vérifier insultes
    if (containsProfanity(trimmedMessage)) {
      setWarningMessage('⚠️ Message refusé : langage inapproprié');
      setTimeout(() => setWarningMessage(''), 3000);
      return;
    }
    
    // ✅ Toutes les vérifications passées
    console.log('💬 Envoi message:', trimmedMessage);
    
    const { error } = await supabase
      .from('match_messages')
      .insert({
        match_id: matchId,
        sender_type: isScorer ? 'scorer' : 'spectator',
        sender_name: getDisplayName(),  // Utilise le nom avec suffix si noteur
        message: trimmedMessage,
        is_system: false
      });
    
    if (error) {
      console.error('❌ Erreur envoi message:', error);
      setWarningMessage('Erreur lors de l\'envoi du message');
      setTimeout(() => setWarningMessage(''), 3000);
      return;
    }
    
    // Mettre à jour les protections
    const now = Date.now();
    setLastSentTime(now);
    setCooldownRemaining(COOLDOWN_MS);
    setRecentMessages(prev => [...prev, now]);
    
    setNewMessage('');
    console.log('✅ Message envoyé');
  };
  
  const sendSystemMessage = async (type) => {
    const messageTexts = {
      pause: '⏸️ Match suspendu',
      resume: '▶️ Match repris',
      delay: '⏰ Début du match retardé',
      start: '🎾 Match commencé !',
      end: '🏁 Match terminé'
    };
    
    const messageText = messageTexts[type];
    if (!messageText) return;
    
    console.log('💬 Envoi message système:', messageText);
    
    const { error } = await supabase
      .from('match_messages')
      .insert({
        match_id: matchId,
        sender_type: 'system',
        sender_name: 'Système',
        message: messageText,
        is_system: true
      });
    
    if (error) {
      console.error('❌ Erreur envoi message système:', error);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // 🎨 Insérer emoji dans le message
  const insertEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  
  // 📝 Sauvegarder le prénom
  const saveName = (name) => {
    const trimmedName = name.trim();
    if (trimmedName) {
      setSenderName(trimmedName);
      const storageKey = isScorer ? 'scorer_name' : 'spectator_name';
      localStorage.setItem(storageKey, trimmedName);
      setShowNamePrompt(false);
    }
  };
  
  // 📝 Obtenir le nom affiché (avec suffix Noteur si scorer)
  const getDisplayName = () => {
    if (!senderName) return isScorer ? 'Noteur' : 'Spectateur';
    return isScorer ? `${senderName} (Noteur)` : senderName;
  };
  
  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
      setWarningMessage(''); // Reset warning quand on ouvre
      
      // Demander le prénom si pas encore renseigné
      if (!senderName) {
        setShowNamePrompt(true);
      }
    }
  };
  
  // Calculer temps restant cooldown (en secondes)
  const cooldownSeconds = Math.ceil(cooldownRemaining / 1000);
  
  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={toggleChat}
        style={{
          position: 'fixed',
          bottom: isScorer ? '20px' : (isOpen ? '520px' : '80px'), // 🆕 Remonter si panneau ouvert
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#1976D2',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999,
          transition: 'bottom 0.3s, transform 0.2s' // 🆕 Animation smooth
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        💬
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            backgroundColor: '#f44336',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>
      
      {/* Panneau de chat */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: '350px',
          maxWidth: 'calc(100vw - 40px)',
          height: '500px',
          maxHeight: 'calc(100vh - 120px)',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 998,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            backgroundColor: '#1976D2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>
                💬 Chat du match
              </div>
              {match && (
                <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                  {match.prenomJoueur1} vs {match.prenomJoueur2}
                </div>
              )}
            </div>
            <button
              onClick={toggleChat}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
          
          {/* Warning message */}
          {warningMessage && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontSize: '13px',
              textAlign: 'center',
              borderBottom: '1px solid #ffb74d'
            }}>
              {warningMessage}
            </div>
          )}
          
          {/* 📝 Prompt prénom (noteur ou spectateur) */}
          {showNamePrompt && (
            <div style={{
              padding: '16px',
              backgroundColor: isScorer ? '#e8f5e9' : '#e3f2fd',
              borderBottom: isScorer ? '1px solid #a5d6a7' : '1px solid #90caf9'
            }}>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: isScorer ? '#2e7d32' : '#1565c0',
                marginBottom: '8px'
              }}>
                {isScorer ? '📢 Noteur du match' : '👋 Bienvenue dans le chat !'}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: isScorer ? '#388e3c' : '#1976D2',
                marginBottom: '12px'
              }}>
                {isScorer 
                  ? 'Renseignez votre prénom pour personnaliser vos messages :'
                  : 'Renseignez votre prénom (optionnel mais recommandé) :'}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder={isScorer ? "Ex: Thomas" : "Votre prénom"}
                  maxLength={20}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      saveName(e.target.value);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: isScorer ? '1px solid #a5d6a7' : '1px solid #90caf9',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = isScorer ? '#4caf50' : '#1976D2'}
                  onBlur={(e) => e.target.style.borderColor = isScorer ? '#a5d6a7' : '#90caf9'}
                  autoFocus
                />
                <button
                  onClick={(e) => {
                    const input = e.target.previousSibling;
                    saveName(input.value);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isScorer ? '#4caf50' : '#1976D2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              </div>
              <button
                onClick={() => {
                  setSenderName(isScorer ? '' : 'Spectateur');
                  setShowNamePrompt(false);
                }}
                style={{
                  marginTop: '8px',
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  color: isScorer ? '#388e3c' : '#1976D2',
                  border: 'none',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {isScorer ? 'Passer (rester "Noteur")' : 'Passer (rester anonyme)'}
              </button>
            </div>
          )}
          
          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: '#f5f5f5'
          }}>
            {messages.length === 0 && (
              <div style={{
                textAlign: 'center',
                color: '#999',
                padding: '20px',
                fontSize: '14px'
              }}>
                Aucun message pour l'instant.
                {isScorer && <div style={{ marginTop: '8px' }}>Envoyez un message aux spectateurs !</div>}
              </div>
            )}
            
            {messages.map((msg) => (
              <div key={msg.id} style={{
                padding: '10px 12px',
                borderRadius: '8px',
                backgroundColor: msg.is_system 
                  ? '#fff3e0' 
                  : (msg.sender_type === 'scorer' ? '#e3f2fd' : 'white'),
                borderLeft: msg.is_system
                  ? '3px solid #ff9800'
                  : (msg.sender_type === 'scorer' ? '3px solid #1976D2' : '3px solid #9e9e9e'),
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#666',
                  marginBottom: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600' }}>
                    {msg.sender_type === 'system' ? '🔔 ' : ''}
                    {msg.sender_name}
                    {msg.sender_type === 'scorer' && ' 📢'}
                  </span>
                  <span>
                    {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div style={{ 
                  fontSize: '14px',
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}>
                  {msg.message}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Boutons système (si noteur) */}
          {isScorer && (
            <div style={{
              padding: '8px 12px',
              display: 'flex',
              gap: '6px',
              backgroundColor: '#f5f5f5',
              borderTop: '1px solid #e0e0e0',
              flexWrap: 'wrap'
            }}>
              <button onClick={() => sendSystemMessage('pause')} style={systemButtonStyle('#ff9800')}>
                ⏸️ Pause
              </button>
              <button onClick={() => sendSystemMessage('resume')} style={systemButtonStyle('#4caf50')}>
                ▶️ Reprise
              </button>
              <button onClick={() => sendSystemMessage('delay')} style={systemButtonStyle('#ff5722')}>
                ⏰ Retard
              </button>
            </div>
          )}
          
          {/* Input */}
          <div style={{
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: 'white',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            position: 'relative'
          }}>
            {/* 🎨 Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '12px',
                right: '12px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000
              }}>
                {/* Réactions */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#666',
                    marginBottom: '6px'
                  }}>
                    😊 Réactions
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(10, 1fr)',
                    gap: '4px'
                  }}>
                    {emojiPalettes.reactions.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => insertEmoji(emoji)}
                        style={{
                          fontSize: '20px',
                          padding: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Tennis */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#666',
                    marginBottom: '6px'
                  }}>
                    🎾 Tennis
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(10, 1fr)',
                    gap: '4px'
                  }}>
                    {emojiPalettes.tennis.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => insertEmoji(emoji)}
                        style={{
                          fontSize: '20px',
                          padding: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Général */}
                <div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#666',
                    marginBottom: '6px'
                  }}>
                    💬 Général
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(10, 1fr)',
                    gap: '4px'
                  }}>
                    {emojiPalettes.general.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => insertEmoji(emoji)}
                        style={{
                          fontSize: '20px',
                          padding: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Indicateur cooldown */}
            {cooldownRemaining > 0 && (
              <div style={{
                fontSize: '11px',
                color: '#ff9800',
                textAlign: 'center'
              }}>
                ⏱️ Cooldown: {cooldownSeconds}s
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '4px' }}>
              {/* Bouton emoji - plus compact */}
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                style={{
                  padding: '10px',
                  fontSize: '18px',
                  backgroundColor: showEmojiPicker ? '#e3f2fd' : 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  minWidth: '44px',
                  flexShrink: 0
                }}
                title="Ajouter un emoji"
              >
                😊
              </button>
              
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Message..."
                disabled={cooldownRemaining > 0}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '10px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  opacity: cooldownRemaining > 0 ? 0.5 : 1
                }}
                onFocus={(e) => e.target.style.borderColor = '#1976D2'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button 
                onClick={sendMessage}
                disabled={!newMessage.trim() || cooldownRemaining > 0}
                style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: (newMessage.trim() && cooldownRemaining === 0) ? '#1976D2' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: (newMessage.trim() && cooldownRemaining === 0) ? 'pointer' : 'not-allowed',
                  fontSize: '20px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: (newMessage.trim() && cooldownRemaining === 0) ? 'scale(1)' : 'scale(0.9)',
                  boxShadow: (newMessage.trim() && cooldownRemaining === 0) ? '0 2px 8px rgba(25,118,210,0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (newMessage.trim() && cooldownRemaining === 0) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.backgroundColor = '#1565c0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (newMessage.trim() && cooldownRemaining === 0) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = '#1976D2';
                  }
                }}
              >
                ➤
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const systemButtonStyle = (color) => ({
  padding: '6px 12px',
  fontSize: '13px',
  backgroundColor: color,
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: '600'
});

export default ChatPanel;